import { useCallback, useEffect, useRef, useState } from "react";
import { useOutletContext, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import {
  listConversations, getConversation, sendAgentMessage, toggleBot as apiToggleBot,
  claimConversation as apiClaim, listQuickReplies,
} from "@/lib/api";
import ConversationList from "@/components/ConversationList";
import ChatFeed from "@/components/ChatFeed";
import ContactProfile from "@/components/ContactProfile";

const REFRESH_MS = 15000;

export default function Dashboard() {
  const { user } = useOutletContext();
  const [params, setParams] = useSearchParams();
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [tag, setTag] = useState("");
  const [conversations, setConversations] = useState([]);
  const [activeId, setActiveId] = useState(params.get("c"));
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [quickReplies, setQuickReplies] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const activeIdRef = useRef(activeId);
  const lastCountRef = useRef(0);

  useEffect(() => { activeIdRef.current = activeId; }, [activeId]);
  useEffect(() => { listQuickReplies().then(setQuickReplies).catch(() => {}); }, []);

  const loadList = useCallback(async () => {
    if (document.hidden) return;
    try {
      const rows = await listConversations(filter, search, tag);
      setConversations(rows);
      const total = rows.reduce((s, c) => s + (c.unread_count || 0), 0);
      if (total > lastCountRef.current && lastCountRef.current !== 0) {
        toast("Nuevo mensaje de WhatsApp", { description: "Un cliente te acaba de escribir." });
      }
      lastCountRef.current = total;
    } catch {
      /* transient */
    } finally {
      setListLoading(false);
    }
  }, [filter, search, tag]);

  const loadConv = useCallback(async (id) => {
    if (document.hidden) return;
    try {
      const { conversation, messages: msgs } = await getConversation(id);
      if (activeIdRef.current !== id) return;
      setActiveConv(conversation);
      setMessages(msgs);
    } catch {
      toast.error("No se pudo cargar la conversación");
    }
  }, []);

  useEffect(() => {
    loadList();
    const id = setInterval(loadList, REFRESH_MS);
    return () => clearInterval(id);
  }, [loadList]);

  useEffect(() => {
    if (!activeId) return;
    loadConv(activeId);
    const id = setInterval(() => activeIdRef.current && loadConv(activeIdRef.current), REFRESH_MS);
    return () => clearInterval(id);
  }, [activeId, loadConv]);

  const handleSelect = (id) => {
    setActiveId(id);
    setParams(id ? { c: id } : {}, { replace: true });
  };

  const patchConv = (p) => setActiveConv((c) => (c ? { ...c, ...p } : c));

  const handleSend = async (text) => {
    setSending(true);
    try {
      const res = await sendAgentMessage(activeConv.id, text);
      if (res.message) setMessages((ms) => [...ms, res.message]);
      if (res.status === "failed") {
        toast.error("Mensaje no entregado", { description: res.error || "WhatsApp rechazó el mensaje." });
        return true;
      }
      patchConv({ bot_enabled: false, needs_human: false, assigned_agent_id: user.user_id, assigned_agent_name: user.name });
      loadList();
      return true;
    } catch (e) {
      toast.error("No se pudo enviar", { description: e?.response?.data?.detail || "Error del servidor" });
      return false;
    } finally {
      setSending(false);
    }
  };

  const handleToggleBot = async (enabled) => {
    try {
      await apiToggleBot(activeConv.id, enabled);
      patchConv({ bot_enabled: enabled, ...(enabled ? { needs_human: false } : {}) });
      toast(enabled ? "Bot activado en este chat" : "Bot pausado en este chat");
      loadList();
    } catch {
      toast.error("No se pudo cambiar el bot");
    }
  };

  const handleClaim = async () => {
    try {
      await apiClaim(activeConv.id);
      patchConv({ bot_enabled: false, needs_human: false, assigned_agent_id: user.user_id, assigned_agent_name: user.name });
      toast.success("Ahora atiendes tú esta conversación");
      loadList();
    } catch {
      toast.error("No se pudo tomar la conversación");
    }
  };

  return (
    <div data-testid="dashboard-shell" className="h-full flex overflow-hidden bg-white">
      <div className={`${activeId ? "hidden" : "flex"} md:flex w-full md:w-auto md:shrink-0 min-w-0 h-full`}>
        <ConversationList
          conversations={conversations} activeId={activeId} onSelect={handleSelect}
          filter={filter} onFilterChange={setFilter} search={search} onSearch={setSearch}
          tag={tag} onTag={setTag} loading={listLoading}
        />
      </div>
      <div className={`${activeId ? "flex" : "hidden"} md:flex flex-1 flex-col w-full min-w-0 h-full`}>
        {activeId && (
          <button type="button" data-testid="back-to-chats-button" onClick={() => handleSelect(null)}
            className="md:hidden shrink-0 flex items-center gap-2 px-4 py-2 text-sm font-semibold text-emerald-800 bg-white border-b border-slate-200">
            <ArrowLeft className="h-4 w-4" />Volver a chats
          </button>
        )}
        <ChatFeed
          conversation={activeId ? activeConv : null} messages={messages} onSend={handleSend}
          onToggleBot={handleToggleBot} onClaim={handleClaim} sending={sending} quickReplies={quickReplies}
        />
      </div>
      {activeId && activeConv && (
        <ContactProfile conversation={activeConv} messagesCount={messages.length} onChange={patchConv} onRefresh={loadList} />
      )}
    </div>
  );
}
